import {
  useMutation,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { breakfastEventsPrefix } from "../queryKeys";
import { circlesSchedulePrefix } from "@/features/circles/queryKeys";
import { trackedDaysPrefix } from "@/hooks/queries/useTrackedDays";
import type { BreakfastEventWithAttendees } from "../types";

/**
 * Cancel a breakfast event (Outlook-style soft delete).
 *
 * ## Why this is no longer a hard DELETE
 *
 * Before the April 2026 "Outlook-style cancellation" refactor this hook
 * issued a blunt `DELETE FROM breakfast_events`. The cascade cleared the
 * attendee rows, which in turn wiped the event out of every invitee's
 * React Query cache — BUT their browsers still held a stale reference in
 * open tabs, sidebars, and potluck modals. Any subsequent interaction
 * (e.g. toggling a potluck item) hit a server RPC that first checked
 * `breakfast_attendees` for the actor, found nothing, and returned an
 * opaque HTTP 403 "You are not an attendee of this event." — the
 * "ghost-event 403" bug.
 *
 * Microsoft Outlook solves this by *flipping the event to Cancelled*
 * rather than deleting it: the row sticks around on every attendee's
 * calendar, tagged "Cancelled", until they explicitly choose to remove
 * it. We do the same thing now:
 *
 *   1. Host taps "Cancel event".
 *   2. This hook calls `soft_cancel_breakfast_event(p_event_id)`
 *      (see `20260424120000_outlook_style_cancellations.sql`), which
 *      flips `breakfast_events.status = 'cancelled'` and fans out one
 *      `EVENT_CANCELLED` bell notification per attendee.
 *   3. The attendee's calendar re-renders the event with the "cancelled"
 *      visual state (strikethrough + red outline + locked potluck
 *      interactions — see `MonthView` / `EventDetailDrawer`).
 *   4. The attendee taps "Remove from calendar" on the bell row, which
 *      hits the user-scoped `remove_event_from_my_calendar` RPC. THAT
 *      is the only place in the app that still issues a hard DELETE,
 *      and only for the caller's own attendee row.
 *
 * The cancellation email still fires here as best-effort so attendees
 * who have email-only notifications enabled (or haven't opened the app
 * in a while) get a copy of the "Event cancelled" message.
 *
 * ## "Fake Success" guard
 *
 * Supabase's JS client returns `{ error: null }` even when RLS silently
 * filters every row out. The underlying RPC raises an exception when
 * the caller isn't the host, so `error` is authoritative here — no need
 * for a `.select()` zero-row check like the previous DELETE needed.
 */
type DeleteCtx = {
  previous: Array<[QueryKey, BreakfastEventWithAttendees[] | undefined]>;
};

export function useDeleteEvent() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<string, Error, string, DeleteCtx>({
    mutationFn: async (eventId: string) => {
      // Snapshot event metadata + attendee roster BEFORE we flip the
      // status. We don't strictly need the "before" list for the soft
      // cancel (the row persists), but the email fan-out still wants
      // the host-excluded list of attendees and that's easiest to pull
      // directly via a SELECT rather than parsing the RPC's return.
      const { data: snapshotEvent } = await supabase
        .from("breakfast_events")
        .select("title, event_date, event_time, event_ends, starts_at, ends_at, event_timezone, location_or_link, host_id")
        .eq("id", eventId)
        .maybeSingle();

      const { data: snapshotAttendees } = await supabase
        .from("breakfast_attendees")
        .select("user_id")
        .eq("event_id", eventId);

      // Soft cancel — flips status='cancelled' + fans out EVENT_CANCELLED
      // bell notifications to every non-host attendee server-side.
      // Idempotent: re-invoking on an already-cancelled event is a no-op.
      const { error } = await supabase.rpc("soft_cancel_breakfast_event", {
        p_event_id: eventId,
      });

      if (error) {
        throw new Error(
          error.message ||
            "Event could not be cancelled. You may not be the host.",
        );
      }

      // Dispatch the "This event was cancelled" email. Best-effort:
      // status has already flipped server-side, so a Resend failure
      // must only warn to console — we must NOT throw and flip the
      // mutation into `onError`, which would rollback the optimistic
      // cache update and confuse the host into thinking nothing
      // happened.
      const hostId = (snapshotEvent?.host_id as string | undefined) ?? null;
      const attendeeIds = (snapshotAttendees ?? [])
        .map((r: { user_id: string }) => r.user_id)
        .filter((id) => !!id && id !== hostId);

      if (snapshotEvent && hostId && attendeeIds.length > 0) {
        try {
          const { error: cancelErr } = await supabase.functions.invoke(
            "send-event-cancellation",
            {
              body: {
                removed_user_ids: attendeeIds,
                reason: "deleted",
                event: {
                  title:            snapshotEvent.title,
                  event_date:       snapshotEvent.event_date,
                  event_time:       snapshotEvent.event_time,
                  event_ends:       snapshotEvent.event_ends,
                  starts_at:        snapshotEvent.starts_at,
                  ends_at:          snapshotEvent.ends_at,
                  event_timezone:   snapshotEvent.event_timezone,
                  location_or_link: snapshotEvent.location_or_link,
                  host_id:          hostId,
                },
              },
            },
          );
          if (cancelErr) {
            console.warn("[useDeleteEvent] send-event-cancellation failed:", cancelErr.message);
          }
        } catch (err) {
          console.warn("[useDeleteEvent] send-event-cancellation threw:", err);
        }
      }

      return eventId;
    },
    onMutate: async (eventId) => {
      await queryClient.cancelQueries({ queryKey: breakfastEventsPrefix });

      const previous = queryClient.getQueriesData<BreakfastEventWithAttendees[]>({
        queryKey: breakfastEventsPrefix,
      });

      // Optimistically flip the row's `status` to `'cancelled'` rather
      // than removing it from every cache slice. This preserves the
      // Outlook-style UX for the host too: the event keeps its slot on
      // their own calendar with the strikethrough treatment until they
      // explicitly "Remove from calendar" via the bell.
      queryClient.setQueriesData<BreakfastEventWithAttendees[] | undefined>(
        { queryKey: breakfastEventsPrefix },
        (old) =>
          old
            ? old.map((ev) =>
                ev.id === eventId ? { ...ev, status: "cancelled" } : ev,
              )
            : old,
      );

      return { previous };
    },
    // Same four-way safeguard stack rationale as `useUpdateEventFull`.
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: breakfastEventsPrefix,
          refetchType: "all",
        }),
        // cross-feature: event detail drawer
        queryClient.invalidateQueries({
          queryKey: ["eventDetails"],
          refetchType: "all",
        }),
        // cross-feature: circles calendar
        queryClient.invalidateQueries({
          queryKey: circlesSchedulePrefix,
          refetchType: "all",
        }),
        // cross-feature: dashboard tracked-days grid
        queryClient.invalidateQueries({
          queryKey: trackedDaysPrefix,
          refetchType: "all",
        }),
      ]);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: breakfastEventsPrefix,
        refetchType: "all",
      });
    },
    onError: (err, _eventId, context) => {
      if (context?.previous) {
        for (const [key, snapshot] of context.previous) {
          queryClient.setQueryData(key, snapshot);
        }
      }
      toast({
        title: "Could not cancel event",
        description: err?.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });
}
